/**
 * @fileoverview Sticky Add to Cart Bar - Synchronized product purchase button
 * 
 * This module implements a sticky add-to-cart bar that appears when users scroll
 * down product pages. It synchronizes with Globo Product Options and Rio variant
 * pickers to display current selections and pricing.
 * 
 * @see {@link https://github.com/yannrapaport/la-pelucherie-sticky-cta|Project Repository}
 * @see {@link ./docs/sticky-add-to-cart.md|Feature Documentation}
 */

(function() {
  'use strict';

  const SELECTORS = {
    stickyContainer: '[data-sticky-add-to-cart]',
    mainButton: '[data-add-to-cart]',
    productForm: '[data-product-form]',
    variantSelect: '[data-variant-select]',
    variantInput: '[data-variant-input]',
    stickyProductVariantBtn: '[data-sticky-product-variant-btn]',
    stickyVariantText: '[data-sticky-variant-text]',
    stickyOptionsBtn: '[data-sticky-options-btn]',
    stickyOptionsText: '[data-sticky-options-text]',
    stickyOptionsCount: '[data-sticky-options-count]',
    stickyPrice: '[data-sticky-price]',
    stickyButton: '[data-sticky-button]',
    stickyButtonText: '[data-sticky-button-text]',
    stickyButtonIcon: '[data-sticky-button-icon]',
    globoApp: '.gpo-app',
    globoGroup: '.gpo-element.gpo-form__group',
    globoElement: '.gpo-element',
    globoInput: '.gpo-input',
    globoSwitch: '.gpo-switch input',
    globoSelect: '.gpo-select',
    globoCheckbox: '.gpo-checkbox input',
    globoRadio: '.gpo-radio input',
    headerInner: '.header__inner',
    productPrice: '.product__price',
    productBlockHeader: '.product__block--product-header'
  };

  // Fallback selectors for finding main add-to-cart button
  const MAIN_BUTTON_SELECTORS = [
    '[data-add-to-cart]',
    '.product-form__cart-submit',
    'button[type="submit"][name="add"]',
    'form[data-product-form] button[type="submit"]'
  ];

  // Selector for required inputs in Globo options
  const REQUIRED_INPUT_SELECTOR =
    'input[required], select[required], textarea[required], ' +
    'input[aria-required="true"], select[aria-required="true"], textarea[aria-required="true"], ' +
    'input[data-required="true"], select[data-required="true"], textarea[data-required="true"], ' +
    'input.gpo-required, select.gpo-required, textarea.gpo-required, ' +
    '.gpo-required input, .gpo-required select, .gpo-required textarea, ' +
    '.gpo-element[data-required="true"] input, .gpo-element[data-required="true"] select, .gpo-element[data-required="true"] textarea';

  const CLASSES = {
    visible: 'is-visible',
    hasVariants: 'has-variants',
    hasOptions: 'has-options',
    loading: 'is-loading'
  };

  // Feature toggles
  const FEATURES = {
    SHOW_VARIANTS: true,
    SHOW_OPTIONS: true,
    SHOW_THUMBNAIL: true
  };

  // Configuration constants
  const CONFIG = {
    SCROLL_THRESHOLD_SHOW: 200,
    SCROLL_THRESHOLD_FALLBACK: 300,
    MAX_BUTTON_FIND_ATTEMPTS: 10,
    MAX_GLOBO_INIT_ATTEMPTS: 10,
    SIBLING_SEARCH_LIMIT: 10,
    Z_INDEX_STICKY: '9999',
    DELAYS: {
      BUTTON_FIND_RETRY: 200,
      GLOBO_INIT: 1000,
      GLOBO_CHANGE: 100,
      GLOBO_UPDATE: 200,
      GLOBO_FINAL: 500,
      PRICE_UPDATE: 50,
      INIT_UPDATE: 100,
    }
  };

  /**
   * Browser detection utilities - cached for performance
   * 
   * Generic utility for detecting browser and device characteristics.
   * Results are cached to avoid repeated user agent parsing.
   * 
   * @namespace BrowserDetection
   * @example
   * const browser = BrowserDetection.detect();
   * if (browser.isChromeMobile) {
   *   // Apply Chrome mobile specific fix
   * }
   */
  const BrowserDetection = {
    /** @private {Object|null} Cached detection results */
    _cache: null,
    
    /**
     * Detect browser and device characteristics
     * @returns {Object} Detection results with properties:
     *   - {boolean} isMobile - True if touch device detected
     *   - {boolean} isChrome - True if Chrome browser detected
     *   - {boolean} isTablet - True if mobile device with width >= 768px
     *   - {boolean} isChromeMobile - True if Chrome on mobile device
     */
    detect() {
      if (this._cache) return this._cache;
      
      const isMobile = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
      const isTablet = isMobile && window.innerWidth >= 768;
      
      this._cache = {
        isMobile,
        isChrome,
        isTablet,
        isChromeMobile: isChrome && isMobile
      };
      
      return this._cache;
    },
    
    /**
     * Reset cached detection results (useful for testing or after orientation change)
     */
    reset() {
      this._cache = null;
    }
  };

  /**
   * Viewport utilities - provides consistent viewport dimension calculations
   * 
   * Generic utility for getting viewport and document dimensions with cross-browser
   * compatibility. Handles differences between browsers and provides fallbacks.
   * 
   * @namespace ViewportUtils
   * @example
   * const scrollY = ViewportUtils.getScrollY();
   * const viewportHeight = ViewportUtils.getHeight();
   */
  const ViewportUtils = {
    /**
     * Get current vertical scroll position
     * @returns {number} Scroll position in pixels from top
     */
    getScrollY() {
      return window.scrollY || window.pageYOffset || 0;
    },
    
    /**
     * Get viewport height
     * @returns {number} Viewport height in pixels
     */
    getHeight() {
      return window.innerHeight || document.documentElement.clientHeight;
    },
    
    /**
     * Get full document height (including scrollable content)
     * Uses multiple fallbacks for cross-browser compatibility
     * @returns {number} Document height in pixels
     */
    getDocumentHeight() {
      return Math.max(
        document.body.scrollHeight,
        document.body.offsetHeight,
        document.documentElement.clientHeight,
        document.documentElement.scrollHeight,
        document.documentElement.offsetHeight
      );
    }
  };

  /**
   * Globo Product Options filtering utilities
   * 
   * Generic utilities for filtering and processing Globo Product Options DOM structure.
   * These utilities handle the complex Globo DOM hierarchy to identify main options
   * vs sub-elements (like text inputs that belong to swatches).
   * 
   * @namespace GloboFilterUtils
   * @example
   * const groups = globoApp.querySelectorAll('.gpo-element.gpo-form__group');
   * const mainOptions = Array.from(groups).filter(g => GloboFilterUtils.isMainOptionGroup(g));
   */
  const GloboFilterUtils = {
    /**
     * Check if a Globo group represents a main option (not a sub-element)
     * 
     * Main options include:
     * - Image swatches and color swatches
     * - Standalone switches/checkboxes
     * 
     * Excludes:
     * - Paragraph elements
     * - Total/price elements
     * - Text inputs that are sub-elements of swatches (data-element-id starting with "text-")
     * 
     * @param {HTMLElement} group - The .gpo-element.gpo-form__group element to check
     * @returns {boolean} True if this is a main option group
     */
    isMainOptionGroup(group) {
      // Exclude paragraphs
      const isParagraph = group.querySelector('.gpo-paragraph') && 
                         !group.querySelector('input[type="text"], input[type="checkbox"], input[type="radio"], select, textarea');
      if (isParagraph) return false;

      // Exclude totals
      if (group.classList.contains('gpo-total')) return false;

      // Exclude text inputs that are sub-elements
      const elementId = group.getAttribute('data-element-id');
      if (elementId && elementId.startsWith('text-')) return false;

      // Include groups with main swatches
      const hasMainSwatch = group.querySelector(
        'input[data-type="image-swatches"], input[data-type="swatches"], ' +
        '.gpo-swatches input[type="radio"], .gpo-swatches input[type="checkbox"]'
      );
      if (hasMainSwatch) return true;

      // Include groups with switches
      const hasSwitch = group.querySelector('.gpo-switch input:checked, input[type="checkbox"].gpo-switch:checked');
      return hasSwitch;
    },
    
    /**
     * Find the label element for a Globo option group
     * 
     * Searches in multiple locations due to Globo's flexible DOM structure:
     * 1. Within the group itself (multiple selector patterns)
     * 2. Parent group/wrapper
     * 3. Previous sibling
     * 4. Wrapper element
     * 
     * @param {HTMLElement} group - The .gpo-element.gpo-form__group element
     * @returns {HTMLElement|null} The label element or null if not found
     */
    findLabel(group) {
      // Try multiple selectors in order of preference
      const selectors = [
        '.gpo-label .label-content',
        '.gpo-label',
        '[data-gpo-label]',
        'label',
        '.gpo-element .gpo-label'
      ];
      
      for (const selector of selectors) {
        const label = group.querySelector(selector);
        if (label) return label;
      }
      
      // Try parent/sibling search
      const parentGroup = group.closest('.gpo-group, .gpo-group__wrapper');
      if (parentGroup) {
        const label = parentGroup.querySelector('.gpo-label, .gpo-label .label-content');
        if (label) return label;
      }
      
      // Try previous sibling
      const siblingLabel = group.previousElementSibling?.querySelector('.gpo-label');
      if (siblingLabel) return siblingLabel;
      
      // Try wrapper
      const wrapper = group.closest('.gpo-group__wrapper');
      if (wrapper) {
        return wrapper.querySelector('.gpo-label, .gpo-label .label-content');
      }
      
      return null;
    }
  };

  /**
   * Sticky Add to Cart Bar Manager
   * 
   * Manages the sticky add-to-cart bar that appears when users scroll down product pages.
   * Handles visibility, content synchronization with Globo/Rio, and user interactions.
   * 
   * @class StickyAddToCart
   * @see {@link ./docs/sticky-add-to-cart.md|Feature Documentation} for detailed implementation guide
   */
  class StickyAddToCart {
    /**
     * Initialize the sticky add-to-cart bar
     * @constructor
     */
    constructor() {
      this.container = document.querySelector(SELECTORS.stickyContainer);
      this.productForm = document.querySelector(SELECTORS.productForm);

      if (!this.container || !this.productForm) {
        return;
      }
      
      // Cache browser detection
      this.browser = BrowserDetection.detect();
      
      // Enable debug mode
      this.debugMode = this._initDebugMode();

      // Cache DOM elements
      this._cacheDOMElements();
      
      // Initialize state
      this.isVisible = false;
      this.globoOptions = [];
      this.globoInitAttempts = 0;
      this.mainButtonRect = null;
      this._loadingTimeout = null;
      this._persoAddonCents = 0; // Supplément perso courant (via event perso:change)
      this._globoApp = null; // Cache globo app reference
      this._headerEl = null; // Cache header reference

      // Find main button
      this.mainButton = this.findMainButton();
      if (!this.mainButton) {
        this._retryFindButton();
      }

      // Initialize
      this.init();
    }

    _initDebugMode() {
      const urlParams = new URLSearchParams(window.location.search);
      const debugParam = urlParams.get('debug');
      return debugParam === 'sticky' || 
             (debugParam === '1' && this.browser.isChromeMobile);
    }

    _cacheDOMElements() {
      // Sticky container elements
      this.productVariantBtn = this.container.querySelector(SELECTORS.stickyProductVariantBtn);
      this.variantTextEl = this.container.querySelector(SELECTORS.stickyVariantText);
      this.optionsBtn = this.container.querySelector(SELECTORS.stickyOptionsBtn);
      this.optionsTextEl = this.container.querySelector(SELECTORS.stickyOptionsText);
      this.optionsCountEl = this.container.querySelector(SELECTORS.stickyOptionsCount);
      this.priceEl = this.container.querySelector(SELECTORS.stickyPrice);
      this.priceCartEl = this.container.querySelector('.sticky-add-to-cart__price-cart');
      this.buttonEl = this.container.querySelector(SELECTORS.stickyButton);
      this.buttonTextEl = this.container.querySelector(SELECTORS.stickyButtonText);
      this.buttonIconEl = this.container.querySelector(SELECTORS.stickyButtonIcon);
      this.innerEl = this.container.querySelector('.sticky-add-to-cart__inner');
      
      // Set variants class
      if (this.productVariantBtn) {
        this.container.classList.add(CLASSES.hasVariants);
      }
    }

    _retryFindButton() {
      let attempts = 0;
      const findButton = () => {
        attempts++;
        this.mainButton = this.findMainButton();

        if (this.mainButton) {
          this.updateVisibility();
        } else if (attempts < CONFIG.MAX_BUTTON_FIND_ATTEMPTS) {
          setTimeout(findButton, CONFIG.DELAYS.BUTTON_FIND_RETRY);
        }
      };
      setTimeout(findButton, CONFIG.DELAYS.BUTTON_FIND_RETRY);
    }

    /**
     * Find the main add-to-cart button using multiple fallback selectors
     * @returns {HTMLElement|null} The button element or null if not found
     */
    findMainButton() {
      // Try product form first
      for (const selector of MAIN_BUTTON_SELECTORS) {
        const button = this.productForm.querySelector(selector);
        if (button) return button;
      }

      // Fall back to global search
      for (const selector of MAIN_BUTTON_SELECTORS) {
        const button = document.querySelector(selector);
        if (button) return button;
      }

      return null;
    }

    init() {
      this.setupScrollListener();
      this.setupGloboListener();
      this.setupClickHandler();
      this.setupPriceListener();
      this.setupVariantListener();
      
      // Initial update with delay to ensure DOM is ready
      setTimeout(() => {
        this.updateVisibility();
        this.updateContent();
      }, CONFIG.DELAYS.INIT_UPDATE);
    }

    setupScrollListener() {
      let ticking = false;
      const browser = this.browser;

      const handleScroll = () => {
        if (!ticking) {
          window.requestAnimationFrame(() => {
            this.updateVisibility();
            
            // Force position update on mobile
            if (browser.isMobile) {
              this.forcePositionUpdate();
              
              if (browser.isChromeMobile && this.isVisible) {
                this.forcePositionChrome();
              } else if (browser.isTablet && this.isVisible) {
                this.forcePositionInline();
              }
            }
            
            // Debug logging
            if (browser.isTablet && this.isVisible && this.debugMode) {
              this.debugPosition();
            }
            
            ticking = false;
          });
          ticking = true;
        }
      };

      // Add event listeners
      window.addEventListener('scroll', handleScroll, { passive: true });
      window.addEventListener('resize', handleScroll, { passive: true });
      window.addEventListener('orientationchange', handleScroll, { passive: true });
      
      // Touch events for mobile
      if (browser.isMobile) {
        let touchTicking = false;
        const handleTouch = () => {
          if (!touchTicking) {
            window.requestAnimationFrame(() => {
              this.forcePositionUpdate();
              if (browser.isChromeMobile && this.isVisible) {
                this.forcePositionChrome();
              } else if (browser.isTablet && this.isVisible) {
                this.forcePositionInline();
              }
              touchTicking = false;
            });
            touchTicking = true;
          }
        };
        window.addEventListener('touchmove', handleTouch, { passive: true });
        window.addEventListener('touchend', handleTouch, { passive: true });
      }

      // Periodic check for button changes
      setInterval(() => {
        if (!this.mainButton || !document.body.contains(this.mainButton)) {
          this.mainButton = null;
          this.updateVisibility();
        }
      }, 1000);

      // Initial check
      this.updateVisibility();
    }

    updateVisibility() {
      // La barre n'apparaît qu'une fois le bouton principal dépassé vers le
      // haut (passé sous l'en-tête fixe) — jamais tant qu'il est à l'écran ou
      // plus bas dans la page (atelier fiche produit, 10/2026). Sans bouton
      // principal repéré : repli sur le défilement.
      const scrollY = ViewportUtils.getScrollY();
      let shouldShow;
      if (this.mainButton) {
        const rect = this.mainButton.getBoundingClientRect();
        shouldShow = rect.bottom < this._getHeaderBottom();
      } else {
        shouldShow = scrollY > CONFIG.SCROLL_THRESHOLD_FALLBACK;
      }

      if (shouldShow && !this.isVisible) {
        this.show();
      } else if (!shouldShow && this.isVisible) {
        this.hide();
      }

      // Store button rect for potential future use
      if (this.mainButton) {
        try {
          this.mainButtonRect = this.mainButton.getBoundingClientRect();
        } catch (e) {
          // Silently handle errors
        }
      }
    }

    /**
     * Force position update for mobile browsers
     */
    forcePositionUpdate() {
      if (!this.container) return;
      void this.container.offsetHeight; // Force reflow
    }
    
    /**
     * Debug function to log sticky position during scroll
     */
    debugPosition() {
      if (!this.container) return;
      
      const rect = this.container.getBoundingClientRect();
      const viewportHeight = ViewportUtils.getHeight();
      const scrollY = ViewportUtils.getScrollY();
      const expectedBottom = viewportHeight;
      const actualBottom = rect.bottom;
      const difference = actualBottom - expectedBottom;
      
      if (Math.abs(difference) > 2) {
        console.log('[Sticky Debug]', {
          scrollY: scrollY.toFixed(0),
          viewportHeight: viewportHeight.toFixed(0),
          expectedBottom: expectedBottom.toFixed(0),
          actualBottom: actualBottom.toFixed(0),
          difference: difference.toFixed(2) + 'px',
          rect: {
            top: rect.top.toFixed(0),
            bottom: rect.bottom.toFixed(0),
            left: rect.left.toFixed(0),
            right: rect.right.toFixed(0)
          },
          computedStyle: {
            position: window.getComputedStyle(this.container).position,
            bottom: window.getComputedStyle(this.container).bottom,
            transform: window.getComputedStyle(this.container).transform,
            parent: this.container.parentElement?.tagName || 'none'
          }
        });
      }
    }
    
    /**
     * Force position continuously for Chrome mobile/tablet
     * 
     * Workaround spécifique pour le bug Chrome mobile où position: fixed ne fonctionne
     * pas correctement pendant le scroll. Utilise position: absolute avec calcul manuel
     * de la position pour simuler un comportement fixed.
     * 
     * @see {@link ./docs/sticky-add-to-cart.md#chrome-mobile-positioning-workaround|Documentation du workaround}
     */
    forcePositionChrome() {
      if (!this.container || !this.isVisible) return;
      
      this.container.setAttribute('data-chrome-fix', 'true');
      this.container.style.display = 'flex';
      this.container.style.visibility = 'visible';
      
      const scrollY = ViewportUtils.getScrollY();
      const viewportHeight = ViewportUtils.getHeight();
      const containerHeight = this.container.offsetHeight || this.container.getBoundingClientRect().height || 80;
      
      // Move to body if needed
      if (this.container.parentElement !== document.body) {
        document.body.appendChild(this.container);
      }
      
      // Calculate position
      const topPosition = scrollY + viewportHeight - containerHeight;
      const topValue = Math.max(0, topPosition) + 'px';
      
      // Apply positioning styles
      this._applyChromePositionStyles(topValue);
      
      // Ensure clickability
      this._ensureClickability();
      
      // Debug logging
      if (this.debugMode) {
        requestAnimationFrame(() => this._debugChromePosition(scrollY, viewportHeight, containerHeight, topPosition));
      }
    }

    _applyChromePositionStyles(topValue) {
      this.container.style.position = 'absolute';
      this.container.style.setProperty('inset', `${topValue} 0px auto 0px`);
      this.container.style.top = topValue;
      this.container.style.left = '0px';
      this.container.style.right = '0px';
      this.container.style.bottom = 'auto';
      this.container.style.width = '100%';
      this.container.style.zIndex = CONFIG.Z_INDEX_STICKY;
      this.container.style.margin = '0';
      this.container.style.pointerEvents = 'auto';
      
      // Ensure background
      const computedBg = window.getComputedStyle(this.container).backgroundColor;
      if (!computedBg || computedBg === 'rgba(0, 0, 0, 0)' || computedBg === 'transparent') {
        this.container.style.backgroundColor = 'var(--color-background, #ffffff)';
      }
      
      // Transform for animation
      if (this.container.classList.contains(CLASSES.visible)) {
        this.container.style.transform = 'translateY(0)';
        this.container.style.webkitTransform = 'translateY(0)';
        this.container.style.opacity = '1';
      } else {
        this.container.style.transform = 'translateY(100%)';
        this.container.style.webkitTransform = 'translateY(100%)';
      }
      
      void this.container.offsetHeight; // Force reflow
    }

    _ensureClickability() {
      if (this.innerEl) {
        this.innerEl.style.pointerEvents = 'auto';
      }
      
      const buttons = this.container.querySelectorAll('button');
      buttons.forEach(btn => {
        btn.style.pointerEvents = 'auto';
      });
    }

    _debugChromePosition(scrollY, viewportHeight, containerHeight, topPosition) {
      if (!this.container || !this.isVisible) return;
      
      const rect = this.container.getBoundingClientRect();
      const expectedBottom = viewportHeight;
      const difference = rect.bottom - expectedBottom;
      
      const debugInfo = {
        scrollY: scrollY.toFixed(0),
        viewportHeight: viewportHeight.toFixed(0),
        containerHeight: containerHeight.toFixed(0),
        calculatedTop: topPosition.toFixed(0),
        actualTop: rect.top.toFixed(0),
        actualBottom: rect.bottom.toFixed(0),
        expectedBottom: expectedBottom.toFixed(0),
        difference: difference.toFixed(2) + 'px',
        inBody: this.container.parentElement === document.body,
        computedPosition: window.getComputedStyle(this.container).position,
        computedTop: window.getComputedStyle(this.container).top
      };
      
      console.log('[Sticky Chrome Debug]', debugInfo);
      this.showDebugInfo(debugInfo);
      
      if (Math.abs(difference) > 2) {
        console.warn('[Sticky Chrome] Position incorrect!', {
          expected: expectedBottom.toFixed(0),
          actual: rect.bottom.toFixed(0),
          difference: difference.toFixed(2) + 'px'
        });
      }
    }
    
    /**
     * Force position via inline styles as fallback for other browsers on tablets
     */
    forcePositionInline() {
      if (!this.container || !this.isVisible) return;
      
      const viewportHeight = ViewportUtils.getHeight();
      const rect = this.container.getBoundingClientRect();
      const expectedBottom = viewportHeight;
      const actualBottom = rect.bottom;
      const difference = actualBottom - expectedBottom;
      const absDifference = Math.abs(difference);
      
      if (absDifference > 2) {
        console.log('[Sticky Fix] Correcting position drift:', {
          expected: expectedBottom.toFixed(0),
          actual: actualBottom.toFixed(0),
          difference: difference.toFixed(2) + 'px'
        });
        
        this.container.style.position = 'fixed';
        this.container.style.bottom = '0px';
        this.container.style.left = '0px';
        this.container.style.right = '0px';
        this.container.style.width = '100%';
        
        if (this.container.classList.contains(CLASSES.visible)) {
          this.container.style.transform = 'translateY(0)';
        }
        
        // Verify fix
        requestAnimationFrame(() => {
          if (this.container) {
            const newRect = this.container.getBoundingClientRect();
            const newDifference = newRect.bottom - ViewportUtils.getHeight();
            if (Math.abs(newDifference) > 1) {
              console.warn('[Sticky Fix] Position still incorrect after fix');
            }
          }
        });
      }
    }

    show() {
      if (!this.container) return;
      this.isVisible = true;
      this.container.classList.add(CLASSES.visible);
      this.container.setAttribute('aria-hidden', 'false');
      
      this.container.style.display = 'flex';
      this.container.style.opacity = '1';
      this.container.style.pointerEvents = 'auto';
      
      this.forcePositionUpdate();
      
      // Apply Chrome fix if needed
      if (this.browser.isChromeMobile) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.forcePositionChrome();
            const rect = this.container.getBoundingClientRect();
            if (rect.width === 0 || rect.height === 0) {
              console.warn('[Sticky Chrome] Container has zero dimensions after fix');
            }
          });
        });
      }
      
      // Update content
      requestAnimationFrame(() => {
        this.updateContent();
      });
    }

    hide() {
      this.isVisible = false;
      this.container.classList.remove(CLASSES.visible);
      this.container.setAttribute('aria-hidden', 'true');
      this.hideDebugInfo();
    }
    
    /**
     * Show debug info on screen for easy debugging on mobile
     */
    showDebugInfo(info) {
      let debugEl = document.getElementById('sticky-debug-info');
      if (!debugEl) {
        debugEl = document.createElement('div');
        debugEl.id = 'sticky-debug-info';
        debugEl.style.cssText = `
          position: fixed !important;
          top: 10px !important;
          left: 10px !important;
          background: rgba(0, 0, 0, 0.9) !important;
          color: white !important;
          padding: 12px !important;
          font-size: 12px !important;
          font-family: monospace !important;
          z-index: 99999 !important;
          border-radius: 6px !important;
          max-width: 320px !important;
          word-wrap: break-word !important;
          pointer-events: auto !important;
          box-shadow: 0 4px 12px rgba(0,0,0,0.3) !important;
          line-height: 1.4 !important;
        `;
        document.body.appendChild(debugEl);
      }
      
      const diffValue = parseFloat(info.difference);
      const diffColor = Math.abs(diffValue) > 2 ? '#ff6b6b' : '#51cf66';
      
      debugEl.innerHTML = `
        <div style="font-weight: bold; margin-bottom: 8px; font-size: 13px;">🔍 Sticky Debug</div>
        <div style="margin-bottom: 4px;">ScrollY: <strong>${info.scrollY}px</strong></div>
        <div style="margin-bottom: 4px;">Viewport: <strong>${info.viewportHeight}px</strong></div>
        <div style="margin-bottom: 4px;">Container: <strong>${info.containerHeight}px</strong></div>
        <div style="margin-bottom: 4px;">Calc Top: <strong>${info.calculatedTop}px</strong></div>
        <div style="margin-bottom: 4px;">Actual Top: <strong>${info.actualTop}px</strong></div>
        <div style="margin-bottom: 4px;">Actual Bottom: <strong>${info.actualBottom}px</strong></div>
        <div style="margin-bottom: 4px;">Expected: <strong>${info.expectedBottom}px</strong></div>
        <div style="margin-bottom: 4px; color: ${diffColor}; font-weight: bold;">
          Diff: <strong>${info.difference}</strong>
        </div>
        <div style="margin-bottom: 4px;">In Body: <strong>${info.inBody ? 'Yes' : 'No'}</strong></div>
        <div style="margin-bottom: 4px;">Position: <strong>${info.computedPosition}</strong></div>
        <div>Top CSS: <strong>${info.computedTop}</strong></div>
        <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid rgba(255,255,255,0.3); font-size: 10px; color: #ccc;">
          Add ?debug=sticky to URL to enable
        </div>
      `;
    }
    
    /**
     * Hide debug info
     */
    hideDebugInfo() {
      const debugEl = document.getElementById('sticky-debug-info');
      if (debugEl) {
        debugEl.remove();
      }
    }

    /**
     * Setup listeners for Globo Product Options integration
     * 
     * Configure les listeners pour détecter les changements d'options Globo :
     * - MutationObserver sur .gpo-app pour les ajouts dynamiques
     * - Event listeners sur tous les inputs Globo
     * - Custom event globo:option-change
     * 
     * @see {@link ./docs/sticky-add-to-cart.md#intégration-globo-product-options|Documentation Globo}
     */
    setupGloboListener() {
      // Globo retiré du thème (V1.5) : aucune intégration à écouter. No-op —
      // on évite la boucle de retry et le MutationObserver sur .gpo-app.
      return;
      // eslint-disable-next-line no-unreachable
      const globoApp = this._getGloboApp();
      if (!globoApp) {
        this.globoInitAttempts++;
        if (this.globoInitAttempts < CONFIG.MAX_GLOBO_INIT_ATTEMPTS) {
          setTimeout(() => this.setupGloboListener(), CONFIG.DELAYS.GLOBO_INIT);
        }
        return;
      }

      // Attach listeners to inputs
      const attachListeners = () => {
        const globoInputs = globoApp.querySelectorAll(
          `${SELECTORS.globoInput}, ${SELECTORS.globoSwitch}, ${SELECTORS.globoSelect}, ${SELECTORS.globoCheckbox}, ${SELECTORS.globoRadio}`
        );

        globoInputs.forEach(input => {
          if (input.dataset.stickyGloboListener === 'true') return;
          
          input.dataset.stickyGloboListener = 'true';
          const handleChange = () => {
            setTimeout(() => {
              this.updateGloboOptions();
              this.updateContent();
            }, CONFIG.DELAYS.GLOBO_CHANGE);
          };

          input.addEventListener('change', handleChange, false);
          input.addEventListener('input', handleChange, false);
        });
      };

      attachListeners();

      // Watch for new inputs added dynamically
      const observer = new MutationObserver((mutations) => {
        const shouldUpdate = mutations.some(m => m.addedNodes.length > 0);
        if (shouldUpdate) {
          setTimeout(() => {
            attachListeners();
            this.updateGloboOptions();
            this.updateContent();
          }, CONFIG.DELAYS.GLOBO_UPDATE);
        }
      });

      observer.observe(globoApp, { childList: true, subtree: true });

      // Listen to custom events from Globo
      document.addEventListener('globo:option-change', () => {
        setTimeout(() => {
          this.updateGloboOptions();
          this.updateContent();
        }, CONFIG.DELAYS.GLOBO_CHANGE);
      });

      // Initial update
      setTimeout(() => {
        this.updateGloboOptions();
        this.updateContent();
      }, CONFIG.DELAYS.GLOBO_FINAL);
    }

    _getGloboApp() {
      // Globo retiré du thème (V1.5). La perso est gérée nativement (perso-options
      // + bridge d'ajout panier). On renvoie null : toutes les branches Globo no-op.
      return null;
    }

    /**
     * Check if an input is filled/active
     */
    isInputFilled(input) {
      if (input.type === 'checkbox' || input.type === 'radio') {
        return input.checked;
      }
      if (input.type === 'text' || input.tagName === 'TEXTAREA') {
        return input.value && input.value.trim() !== '';
      }
      if (input.tagName === 'SELECT') {
        return input.value && input.value !== '' && input.value !== '0';
      }
      return false;
    }

    /**
     * Check if a group has any active input
     */
    hasActiveInput(group) {
      const selectors = [
        'input[data-type="image-swatches"]:checked',
        '.gpo-swatches input:checked',
        'input[data-type="swatches"]:checked',
        SELECTORS.globoSwitch + ':checked',
        'input[type="checkbox"].gpo-switch:checked',
        '.gpo-switch input[type="checkbox"]:checked',
        'input[type="checkbox"][class*="switch"]:checked'
      ];
      
      if (group.querySelector(selectors.join(', '))) return true;
      
      const textInputs = group.querySelectorAll(SELECTORS.globoInput + ', input[type="text"].gpo-input, textarea.gpo-input');
      if (Array.from(textInputs).some(input => this.isInputFilled(input) && input.offsetParent !== null)) return true;
      
      const selects = group.querySelectorAll(SELECTORS.globoSelect + ', select.gpo-select');
      if (Array.from(selects).some(select => this.isInputFilled(select) && select.offsetParent !== null)) return true;
      
      if (group.querySelector(SELECTORS.globoCheckbox + ':checked, input[type="checkbox"]:checked:not(.gpo-switch)')) return true;
      if (group.querySelector(SELECTORS.globoRadio + ':checked, input[type="radio"]:checked')) return true;
      
      return false;
    }

    isOptionReady(group, searchScope) {
      const requiredInputs = searchScope.querySelectorAll(REQUIRED_INPUT_SELECTOR);
      const groupRequiredInputs = group.querySelectorAll(REQUIRED_INPUT_SELECTOR);
      const allRequiredInputs = Array.from(new Set([...requiredInputs, ...groupRequiredInputs]));

      const groupIsRequired = group.hasAttribute('data-required') ||
                             group.classList.contains('gpo-required') ||
                             group.closest('.gpo-required, [data-required="true"]') !== null;

      // Check switch-activated text inputs
      if (group.querySelector(SELECTORS.globoSwitch + ':checked, input[type="checkbox"].gpo-switch:checked')) {
        const parentWrapper = group.closest('.gpo-group__wrapper');
        const searchContainer = parentWrapper || group;
        const allTextInputs = Array.from(searchContainer.querySelectorAll('input[type="text"], textarea'));
        
        const visibleTextInputs = allTextInputs.filter(input =>
          input.type !== 'hidden' &&
          input.style.display !== 'none' &&
          input.offsetParent !== null &&
          !input.disabled &&
          !input.readOnly
        );

        const requiredTextInputs = visibleTextInputs.filter(input => {
          if (input.hasAttribute('required') || input.getAttribute('aria-required') === 'true') return true;
          const label = input.closest('.gpo-element.gpo-form__group')?.querySelector('.gpo-label.required');
          if (label) return true;
          const parentGroup = input.closest('.gpo-element.gpo-form__group');
          return parentGroup && (parentGroup.classList.contains('gpo-required') || parentGroup.hasAttribute('data-required'));
        });

        if (requiredTextInputs.length > 0) {
          return requiredTextInputs.every(input => input.value && input.value.trim() !== '');
        } else if (visibleTextInputs.length > 0) {
          const firstInput = visibleTextInputs[0];
          return firstInput.value && firstInput.value.trim() !== '';
        }
      }

      // Check required group
      if (groupIsRequired && allRequiredInputs.length === 0) {
        const allInputsInGroup = searchScope.querySelectorAll('input, select, textarea');
        const visibleInputs = Array.from(allInputsInGroup).filter(input =>
          input.type !== 'hidden' &&
          input.style.display !== 'none' &&
          input.offsetParent !== null
        );

        if (visibleInputs.length > 0) {
          return visibleInputs.some(input => this.isInputFilled(input));
        }
      }

      // No required inputs means ready
      if (allRequiredInputs.length === 0) {
        return true;
      }

      // Check all required inputs are filled
      for (const input of allRequiredInputs) {
        if (input.type === 'hidden' || input.style.display === 'none' || input.offsetParent === null) {
          continue;
        }

        let isFilled = false;
        if (input.type === 'checkbox' || input.type === 'radio') {
          isFilled = input.checked;
        } else if (input.type === 'text' || input.tagName === 'TEXTAREA') {
          isFilled = input.value && input.value.trim() !== '';
        } else if (input.tagName === 'SELECT') {
          isFilled = input.value && input.value !== '' && input.value !== '0';
        }

        if (!isFilled) {
          return false;
        }
      }

      return true;
    }

    /**
     * Update the list of active and complete Globo options
     * 
     * Détecte les options Globo actives et complètes (tous les champs requis remplis).
     * Utilise GloboFilterUtils pour filtrer les options principales vs sous-éléments.
     * 
     * @see {@link ./docs/sticky-add-to-cart.md#détection-des-options|Documentation détection}
     */
    updateGloboOptions() {
      const globoApp = this._getGloboApp();
      if (!globoApp) {
        this.globoOptions = [];
        return;
      }

      const options = [];
      const globoGroups = globoApp.querySelectorAll(SELECTORS.globoGroup);
      const finalGroups = Array.from(globoGroups).filter(group => GloboFilterUtils.isMainOptionGroup(group));

      finalGroups.forEach((group) => {
        const labelEl = GloboFilterUtils.findLabel(group);
        if (!labelEl) return;

        const label = labelEl.textContent.trim();
        if (!label) return;

        const searchScope = group;
        const isActive = this.hasActiveInput(searchScope);
        const isReady = this.isOptionReady(group, searchScope);

        if (!isActive || !isReady) return;

        // Get value from text input or select
        const value = this._getOptionValue(searchScope, group);

        // Avoid duplicates
        const exists = options.some(opt => opt.label === label);
        if (!exists) {
          options.push({ label, value });
        }
      });

      // Fallback method if no groups found
      if (options.length === 0 && finalGroups.length === 0) {
        const alternativeOptions = this._getAlternativeGloboOptions(globoApp);
        if (alternativeOptions.length > 0) {
          this.globoOptions = alternativeOptions;
          return;
        }
      }

      this.globoOptions = options;
    }

    _getOptionValue(searchScope, group) {
      const textInputs = searchScope.querySelectorAll(
        SELECTORS.globoInput + ', input[type="text"].gpo-input, .gpo-input[type="text"], textarea.gpo-input'
      );
      const hasActiveTextInput = Array.from(textInputs).some(
        input => this.isInputFilled(input) && input.offsetParent !== null
      );
      
      if (hasActiveTextInput) {
        const textInput = searchScope.querySelector(
          SELECTORS.globoInput + ', input[type="text"].gpo-input, .gpo-input[type="text"], textarea.gpo-input'
        ) || group.querySelector(
          SELECTORS.globoInput + ', input[type="text"].gpo-input, .gpo-input[type="text"], textarea.gpo-input'
        );
        if (textInput && textInput.value) {
          return textInput.value.trim();
        }
      }
      
      const selects = searchScope.querySelectorAll(
        SELECTORS.globoSelect + ', select.gpo-select, .gpo-select, select'
      );
      const hasActiveSelect = Array.from(selects).some(
        select => this.isInputFilled(select) && select.offsetParent !== null
      );
      
      if (hasActiveSelect) {
        const selectInput = searchScope.querySelector(
          SELECTORS.globoSelect + ', select.gpo-select, .gpo-select, select'
        ) || group.querySelector(SELECTORS.globoSelect + ', select.gpo-select, .gpo-select');
        if (selectInput && selectInput.value) {
          const selectedOption = selectInput.options[selectInput.selectedIndex];
          return selectedOption ? selectedOption.text : selectInput.value;
        }
      }
      
      return '';
    }

    _getAlternativeGloboOptions(globoApp) {
      const allInputs = globoApp.querySelectorAll(
        'input[type="checkbox"], input[type="text"], input[type="radio"], select, textarea'
      );
      const uniqueGroupsMap = new Map();

      allInputs.forEach(input => {
        if (input.type === 'hidden' || input.style.display === 'none' || input.offsetParent === null) {
          return;
        }

        if (!this.isInputFilled(input)) return;

        let groupContainer = input.closest('.gpo-element.gpo-form__group') || input.closest('.gpo-element');
        if (!groupContainer) return;

        // Handle text inputs that belong to swatches
        const elementId = groupContainer.getAttribute('data-element-id');
        if (elementId && elementId.startsWith('text-')) {
          let swatchParent = null;
          let current = groupContainer.previousElementSibling;
          let checked = 0;
          while (current && checked < CONFIG.SIBLING_SEARCH_LIMIT) {
            if (current.matches('.gpo-element.gpo-form__group')) {
              const hasSwatch = current.querySelector(
                'input[data-type="image-swatches"], .gpo-swatches input[type="radio"], .gpo-swatches input[type="checkbox"]'
              );
              if (hasSwatch) {
                swatchParent = current;
                break;
              }
            }
            current = current.previousElementSibling;
            checked++;
          }
          if (swatchParent) {
            groupContainer = swatchParent;
          } else {
            return;
          }
        }

        if (!uniqueGroupsMap.has(groupContainer)) {
          uniqueGroupsMap.set(groupContainer, { container: groupContainer, hasActiveInput: true });
        }
      });

      const alternativeOptions = [];
      uniqueGroupsMap.forEach((groupData, groupContainer) => {
        if (!GloboFilterUtils.isMainOptionGroup(groupContainer)) return;

        const searchScope = groupContainer;
        const isReady = this.isOptionReady(groupContainer, searchScope);
        if (!isReady) return;

        const labelEl = GloboFilterUtils.findLabel(groupContainer);
        if (!labelEl) return;

        const label = labelEl.textContent.trim();
        if (!label || label === '') return;

        const value = this._getOptionValue(searchScope, groupContainer);
        alternativeOptions.push({ label, value });
      });

      return alternativeOptions;
    }

    updateContent() {
      this.updateGloboOptions();
      this.updateOptionsDisplay();
      this.updatePrice();
      this.updateButtonState();
      this.updateVariantFromForm();
      
      if (this.isVisible) {
        this.checkWrap();
      }
    }

    /**
     * Check if the sticky bar content has wrapped to multiple rows
     */
    checkWrap() {
      // Wrap detection is currently handled via CSS responsive breakpoints
      // This method is a placeholder for future dynamic wrap detection if needed
    }

    updateOptionsDisplay() {
      const globoApp = this._getGloboApp();
      const hasGlobo = globoApp !== null;
      
      if (!hasGlobo) {
        if (this.optionsBtn) this.optionsBtn.style.display = 'none';
        this.container.classList.remove(CLASSES.hasOptions);
        return;
      }

      if (this.optionsBtn) this.optionsBtn.style.display = '';
      this.container.classList.add(CLASSES.hasOptions);

      this.updateGloboOptions();
      const hasIncompleteOptions = this.hasIncompleteGloboOptions(globoApp);
      const hasCompleteOptions = this.globoOptions && this.globoOptions.length > 0;

      if (this.optionsTextEl) {
        this.optionsTextEl.textContent = 'Personnalisation';
      }

      if (this.optionsCountEl) {
        if (hasIncompleteOptions) {
          this.optionsCountEl.textContent = '';
          if (this.optionsBtn) {
            this.optionsBtn.classList.remove('has-options');
          }
        } else if (hasCompleteOptions) {
          const count = this.globoOptions.length;
          const text = count === 1 ? 'option' : 'options';
          this.optionsCountEl.textContent = `${count} ${text}`;
          if (this.optionsBtn) {
            this.optionsBtn.classList.add('has-options');
          }
        } else {
          this.optionsCountEl.textContent = '';
          if (this.optionsBtn) {
            this.optionsBtn.classList.remove('has-options');
          }
        }
      }
    }

    /**
     * Check if there are Globo options that are active but incomplete
     */
    hasIncompleteGloboOptions(globoApp) {
      if (!globoApp) return false;

      const globoGroups = globoApp.querySelectorAll(SELECTORS.globoGroup);
      
      for (const group of globoGroups) {
        const isParagraph = group.querySelector('.gpo-paragraph') && 
                           !group.querySelector('input[type="text"], input[type="checkbox"], input[type="radio"], select, textarea');
        if (isParagraph) continue;
        if (group.classList.contains('gpo-total')) continue;

        if (!GloboFilterUtils.isMainOptionGroup(group)) continue;

        const isActive = this.hasActiveInput(group);
        if (isActive) {
          const isReady = this.isOptionReady(group, group);
          if (!isReady) {
            return true;
          }
        }
      }

      return false;
    }

    /**
     * Update the displayed price including Globo supplements
     * 
     * Calcule le prix total = prix de base + suppléments Globo.
     * Gère les formats de prix européens (virgule) et US (point).
     * 
     * @see {@link ./docs/sticky-add-to-cart.md#synchronisation-du-prix|Documentation prix}
     */
    updatePrice() {
      if (!this.priceEl) return;

      const basePriceCents = this._getBasePrice();
      const optionsPriceCents = this._persoAddonCents || 0;
      const totalPriceCents = basePriceCents + optionsPriceCents;
      const formattedPrice = this._formatPrice(totalPriceCents);

      this.priceEl.innerHTML = '';
      this.priceEl.textContent = formattedPrice;
    }

    _getBasePrice() {
      const priceSelectors = [
        SELECTORS.productPrice + ' [data-price]',
        SELECTORS.productPrice + ' .money',
        SELECTORS.productPrice + ' span.money',
        SELECTORS.productPrice + ' span:not([data-compare-price]):not(.unit-price):not(s)',
        '[data-product-price]',
        SELECTORS.productPrice
      ];

      let priceEl = null;
      for (const selector of priceSelectors) {
        priceEl = document.querySelector(selector);
        if (priceEl) break;
      }

      if (!priceEl) return 0;

      const priceText = this._extractPriceText(priceEl);
      return this._parsePriceToCents(priceText);
    }

    _extractPriceText(priceEl) {
      const clone = priceEl.cloneNode(true);
      const allNested = clone.querySelectorAll('*');
      
      allNested.forEach(el => {
        const text = el.textContent || el.innerText || '';
        if (text.trim()) {
          const textNode = document.createTextNode(text);
          if (el.parentNode) {
            el.parentNode.replaceChild(textNode, el);
          }
        } else {
          el.remove();
        }
      });

      let priceText = clone.textContent || clone.innerText || '';
      if (!priceText || priceText.trim() === '') {
        priceText = priceEl.textContent || priceEl.innerText || '';
      }

      // Clean up price text
      const cleanupPatterns = ['Prix', '/', 'à l\'unité', 'Par', 'Prix normal', 'unité'];
      cleanupPatterns.forEach(pattern => {
        priceText = priceText.split(pattern)[0].trim();
      });

      priceText = priceText.replace(/<[^>]*>/g, '').trim();
      priceText = priceText.replace(/&nbsp;/g, ' ').trim();
      priceText = priceText.replace(/&[a-z]+;/gi, '').trim();
      priceText = priceText.replace(/\s+/g, ' ').trim();

      return priceText;
    }

    _getGloboPrice() {
      const globoApp = this._getGloboApp();
      if (!globoApp) return 0;

      const globoTotalSelectors = [
        '.gpo-total .money',
        '.gpo-total-text .money',
        '.gpo-total span.money',
        '.gpo-total-text span.money'
      ];

      let globoTotalEl = null;
      for (const selector of globoTotalSelectors) {
        globoTotalEl = globoApp.querySelector(selector);
        if (globoTotalEl) break;
      }

      if (!globoTotalEl) return 0;

      const nestedMoney = globoTotalEl.querySelector('.money');
      const moneyEl = nestedMoney || globoTotalEl;
      const moneyText = moneyEl.textContent || moneyEl.innerText || '';

      return moneyText.trim() ? this._parsePriceToCents(moneyText) : 0;
    }

    _parsePriceToCents(priceText) {
      if (!priceText) return 0;

      let cleaned = priceText.replace(/[^\d.,\s]/g, '').trim();

      // Handle decimal separators
      if (cleaned.includes(',') && !cleaned.includes('.')) {
        cleaned = cleaned.replace(',', '.');
      } else if (cleaned.includes(',') && cleaned.includes('.')) {
        const lastComma = cleaned.lastIndexOf(',');
        const lastDot = cleaned.lastIndexOf('.');
        if (lastComma > lastDot) {
          cleaned = cleaned.replace(/\./g, '').replace(',', '.');
        } else {
          cleaned = cleaned.replace(/,/g, '');
        }
      } else if (cleaned.includes(',')) {
        cleaned = cleaned.replace(',', '.');
      }

      cleaned = cleaned.replace(/\s/g, '');
      const amount = parseFloat(cleaned);
      return isNaN(amount) ? 0 : Math.round(amount * 100);
    }

    _formatPrice(totalPriceCents) {
      let formattedPrice = '';
      if (window.theme && window.theme.formatMoney) {
        formattedPrice = window.theme.formatMoney(totalPriceCents);
      } else if (typeof formatMoney !== 'undefined') {
        formattedPrice = formatMoney(totalPriceCents);
      } else {
        // Gère les 4 tokens de money_format Shopify, pas seulement {{amount}} :
        // un changement de format boutique ne doit pas laisser le placeholder brut à l'écran.
        const moneyFormat = window.theme?.moneyFormat || '{{amount}} €';
        const token = (moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/) || [])[1] || 'amount';
        const comma = token.includes('comma_separator');
        const noDecimals = token.includes('no_decimals');
        let amount = noDecimals
          ? String(Math.round(totalPriceCents / 100))
          : (totalPriceCents / 100).toFixed(2).replace(/\.00$/, '');
        if (comma) amount = amount.replace('.', ',');
        formattedPrice = moneyFormat.replace(/\{\{\s*\w+\s*\}\}/, amount);
      }

      formattedPrice = formattedPrice.replace(/<[^>]*>/g, '').trim();
      return formattedPrice;
    }

    updateButtonState() {
      if (!this.buttonEl || !this.buttonTextEl) return;

      const mainButton = this.productForm.querySelector(SELECTORS.mainButton);
      if (!mainButton) return;
      
      const isDisabled = mainButton.disabled || mainButton.hasAttribute('aria-disabled');
      
      if (isDisabled) {
        this.buttonEl.disabled = true;
        this.buttonEl.setAttribute('aria-disabled', 'true');
      } else {
        this.buttonEl.disabled = false;
        this.buttonEl.removeAttribute('aria-disabled');
      }

      const mainButtonText = mainButton.querySelector('[data-add-to-cart-text]');
      if (mainButtonText) {
        this.buttonTextEl.textContent = mainButtonText.textContent.trim();
      } else {
        const addToCartText = isDisabled 
          ? (mainButton.getAttribute('data-lang-unavailable') || mainButton.getAttribute('data-lang-sold-out') || 'Indisponible')
          : (mainButton.getAttribute('data-lang-available') || 'Ajouter au panier');
        this.buttonTextEl.textContent = addToCartText;
      }
    }

    setupPriceListener() {
      // Perso native (V1.5) : le supplément vient de l'event document `perso:change`
      // émis par <perso-options> à chaque changement d'option.
      document.addEventListener('perso:change', (e) => {
        this._persoAddonCents = (e.detail && e.detail.addonCents) || 0;
        this.updatePrice();
      });
    }

    /**
     * Setup listeners for Rio variant picker integration
     * 
     * Écoute l'événement product:variant-change émis par Rio et synchronise
     * l'affichage de la variante, du prix et de l'état du bouton.
     * 
     * @see {@link ./docs/sticky-add-to-cart.md#écoute-des-changements-de-variante|Documentation Rio}
     */
    setupVariantListener() {
      document.addEventListener('product:variant-change', (e) => {
        if (e.detail && e.detail.variant) {
          this.updateVariant(e.detail.variant);
          setTimeout(() => {
            this.updatePrice();
            this.updateButtonState();
          }, CONFIG.DELAYS.PRICE_UPDATE);
        }
      });
      
      const mainButton = this.productForm.querySelector(SELECTORS.mainButton);
      if (mainButton) {
        const buttonObserver = new MutationObserver(() => {
          const isDisabled = mainButton.disabled || mainButton.hasAttribute('aria-disabled');
          const isAvailable = !isDisabled;
          this.updateAvailabilityState(isAvailable);
          this.updateButtonState();
          if (isAvailable) {
            setTimeout(() => {
              this.updatePrice();
            }, CONFIG.DELAYS.PRICE_UPDATE);
          }
        });
        buttonObserver.observe(mainButton, { 
          attributes: true, 
          attributeFilter: ['disabled', 'aria-disabled'] 
        });
      }
    }

    updateVariant(variant) {
      if (!this.variantTextEl || !this.productVariantBtn) return;

      const isAvailable = variant.available !== false;
      let variantText = '';
      
      if (variant.title && variant.title !== 'Default Title') {
        variantText = variant.title;
      } else {
        const options = [];
        if (variant.option1) options.push(variant.option1);
        if (variant.option2) options.push(variant.option2);
        if (variant.option3) options.push(variant.option3);
        if (options.length > 0) {
          variantText = options.join(' / ');
        }
      }
      
      if (variantText) {
        this.variantTextEl.textContent = variantText;
        this.productVariantBtn.classList.add('has-variant');
        
        if (!isAvailable) {
          this.variantTextEl.classList.add('is-unavailable');
          this.productVariantBtn.classList.add('has-unavailable-variant');
        } else {
          this.variantTextEl.classList.remove('is-unavailable');
          this.productVariantBtn.classList.remove('has-unavailable-variant');
        }
      } else {
        this.variantTextEl.textContent = '';
        this.productVariantBtn.classList.remove('has-variant');
        this.variantTextEl.classList.remove('is-unavailable');
        this.productVariantBtn.classList.remove('has-unavailable-variant');
      }
      
      this.updateAvailabilityState(isAvailable);
    }
    
    /**
     * Update price and button visibility based on variant availability
     */
    updateAvailabilityState(isAvailable) {
      if (!isAvailable) {
        if (this.priceCartEl) {
          this.priceCartEl.style.display = 'none';
        }
      } else {
        if (this.priceCartEl) {
          this.priceCartEl.style.display = '';
          void this.priceCartEl.offsetHeight;
        }
      }
    }

    /**
     * Get current variant from form and update display
     * 
     * Utilise 5 méthodes de fallback pour détecter la variante actuelle :
     * 1. Variant input (window.theme.product.variants)
     * 2. Rio pickers (match options sélectionnées)
     * 3. Construction depuis DOM
     * 4. Single select
     * 5. Variant display element
     * 
     * @see {@link ./docs/sticky-add-to-cart.md#détection-de-variante-au-chargement|Documentation détection}
     */
    updateVariantFromForm() {
      if (!this.variantTextEl || !this.productForm) return;

      // Method 1: Try variant input
      const variantInput = this.productForm.querySelector(SELECTORS.variantInput);
      if (variantInput && variantInput.value) {
        const variant = this._getVariantById(parseInt(variantInput.value, 10));
        if (variant) {
          this.updateVariant(variant);
          return;
        }
      }
      
      // Method 2: Try matching options to variant
      const variant = this._getVariantFromOptions();
      if (variant) {
        this.updateVariant(variant);
        return;
      }

      // Method 3: Fallback - construct from DOM
      const constructedVariant = this._constructVariantFromDOM();
      if (constructedVariant) {
        this.updateVariant(constructedVariant);
        return;
      }

      // Method 4: Single select fallback
      const singleSelectVariant = this._getVariantFromSingleSelect();
      if (singleSelectVariant) {
        this._applyVariantFromSelect(singleSelectVariant);
        return;
      }

      // Method 5: Variant display element
      const displayVariant = this._getVariantFromDisplay();
      if (displayVariant) {
        this.updateVariant(displayVariant);
        return;
      }

      // No variant found - clear display
      this._clearVariantDisplay();
    }

    _getVariantById(variantId) {
      if (!window.theme?.product?.variants) return null;
      return window.theme.product.variants.find(v => v.id === variantId) || null;
    }

    _getVariantFromOptions() {
      if (!window.theme?.product?.variants) return null;

      const optionSelects = this.productForm.querySelectorAll('select[data-option-index], select[name*="option"]');
      let selectedOptionValues = [];
      
      if (optionSelects.length > 0) {
        selectedOptionValues = Array.from(optionSelects).map(select => {
          const option = select.options[select.selectedIndex];
          return option ? option.value.trim() : null;
        }).filter(Boolean);
      } else {
        const variantSelect = this.productForm.querySelector(SELECTORS.variantSelect);
        if (variantSelect && variantSelect.value) {
          selectedOptionValues = [variantSelect.value];
        }
      }

      if (selectedOptionValues.length === 0) return null;

      return window.theme.product.variants.find(v => {
        const variantOptions = [v.option1, v.option2, v.option3].filter(Boolean);
        if (variantOptions.length !== selectedOptionValues.length) return false;
        
        return variantOptions.every((opt, index) => {
          const selectedValue = selectedOptionValues[index];
          return opt === selectedValue || opt.toLowerCase() === selectedValue.toLowerCase();
        });
      }) || null;
    }

    _constructVariantFromDOM() {
      const optionSelects = this.productForm.querySelectorAll('select[data-option-index], select[name*="option"]');
      if (optionSelects.length === 0) return null;

      const selectedOptions = Array.from(optionSelects).map(select => {
        const option = select.options[select.selectedIndex];
        return option ? option.text.trim() : null;
      }).filter(Boolean);

      if (selectedOptions.length === 0) return null;

      const mainButton = this.productForm.querySelector(SELECTORS.mainButton);
      const isAvailable = mainButton ? 
        !(mainButton.disabled || mainButton.hasAttribute('aria-disabled')) : true;

      return {
        title: selectedOptions.join(' / '),
        option1: selectedOptions[0] || '',
        option2: selectedOptions[1] || '',
        option3: selectedOptions[2] || '',
        available: isAvailable
      };
    }

    _getVariantFromSingleSelect() {
      const variantSelect = this.productForm.querySelector(SELECTORS.variantSelect);
      if (!variantSelect || !variantSelect.value) return null;
      
      const selectedOption = variantSelect.options[variantSelect.selectedIndex];
      return selectedOption && selectedOption.text ? selectedOption.text.trim() : null;
    }

    _applyVariantFromSelect(optionText) {
      this.variantTextEl.textContent = optionText;
      if (this.productVariantBtn) {
        this.productVariantBtn.classList.add('has-variant');
        
        const mainButton = this.productForm.querySelector(SELECTORS.mainButton);
        const isAvailable = mainButton ? 
          !(mainButton.disabled || mainButton.hasAttribute('aria-disabled')) : true;
        
        const isUnavailableFromText = optionText.toLowerCase().includes('sold out') || 
                                     optionText.toLowerCase().includes('indisponible') ||
                                     optionText.toLowerCase().includes('rupture');
        const isUnavailable = !isAvailable || isUnavailableFromText;
        
        if (isUnavailable) {
          this.variantTextEl.classList.add('is-unavailable');
          this.productVariantBtn.classList.add('has-unavailable-variant');
          this.updateAvailabilityState(false);
        } else {
          this.variantTextEl.classList.remove('is-unavailable');
          this.productVariantBtn.classList.remove('has-unavailable-variant');
          this.updateAvailabilityState(true);
        }
      }
    }

    _getVariantFromDisplay() {
      const variantDisplay = this.productForm.querySelector(
        '.product__variant-title, [data-variant-title], .variant-title'
      );
      if (!variantDisplay || !variantDisplay.textContent) return null;
      
      const variantText = variantDisplay.textContent.trim();
      if (!variantText || variantText === 'Default Title') return null;

      const mainButton = this.productForm.querySelector(SELECTORS.mainButton);
      const isAvailable = mainButton ? 
        !(mainButton.disabled || mainButton.hasAttribute('aria-disabled')) : true;

      return {
        title: variantText,
        option1: variantText,
        available: isAvailable
      };
    }

    _clearVariantDisplay() {
      this.variantTextEl.textContent = '';
      if (this.productVariantBtn) {
        this.productVariantBtn.classList.remove('has-variant');
        this.productVariantBtn.classList.remove('has-unavailable-variant');
      }
      this.variantTextEl.classList.remove('is-unavailable');
      this.updateAvailabilityState(true);
    }

    setupClickHandler() {
      if (!this.buttonEl) return;

      this.buttonEl.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.triggerAddToCart();
      });

      if (this.productVariantBtn) {
        this.productVariantBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          // On desktop, scroll to top to avoid cropping the title
          // On mobile, scroll to selection area
          if (!BrowserDetection.detect().isMobile) {
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
          } else {
            this.scrollTo('selection');
          }
        });
      }

      if (this.optionsBtn) {
        this.optionsBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          // On desktop, scroll to top to avoid cropping the title
          // On mobile, scroll to selection area
          if (!BrowserDetection.detect().isMobile) {
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
          } else {
            this.scrollTo('selection');
          }
        });
      }
    }

    /**
     * Unified scroll method for navigating to selection areas
     * 
     * Scroll vers les sections de sélection (variantes ou options Globo).
     * Les deux boutons (variante et personnalisation) scrollent vers la même zone.
     * 
     * @param {string} mode - 'selection' pour variantes, 'options' pour Globo (non utilisé actuellement)
     */
    scrollTo(mode = 'selection') {
      let target = null;
      
      if (mode === 'options') {
        target = this._getGloboApp();
      } else {
        target = document.querySelector(SELECTORS.productBlockHeader);
      }
        
      if (!target) {
        target = this._getGloboApp();
      }
      
      if (!target) return;

      const offset = this._calculateScrollOffset(mode);
      const currentScroll = ViewportUtils.getScrollY();
      const rect = target.getBoundingClientRect();
      const targetTop = rect.top + currentScroll;
      const finalPosition = targetTop - offset;
      
      window.scrollTo({
        top: Math.max(0, finalPosition),
        behavior: 'smooth'
      });
    }

    // Bas de l'en-tête fixe à l'écran (0 s'il a défilé hors de vue) : un
    // bouton passé dessous n'est plus visible.
    _getHeaderBottom() {
      if (!this._headerEl) {
        this._headerEl = document.querySelector(SELECTORS.headerInner);
      }
      return this._headerEl ? Math.max(0, this._headerEl.getBoundingClientRect().bottom) : 0;
    }

    _calculateScrollOffset(mode) {
      if (!this._headerEl) {
        this._headerEl = document.querySelector(SELECTORS.headerInner);
      }
      
      if (mode === 'options') {
        return this._headerEl ? this._headerEl.getBoundingClientRect().height + 20 : 20;
      } else {
        return this._headerEl ? this._headerEl.getBoundingClientRect().height + 150 : 150;
      }
    }
    
    // Compatibility aliases
    scrollToSelection() {
      this.scrollTo('selection');
    }
    
    scrollToOptionsSection() {
      this.scrollTo('options');
    }

    triggerAddToCart() {
      if (!this.productForm) return;

      // Clear any pending timeout
      if (this._loadingTimeout) {
        clearTimeout(this._loadingTimeout);
        this._loadingTimeout = null;
      }

      // Add loading state
      if (this.buttonEl) {
        this.buttonEl.classList.add(CLASSES.loading);
        this.buttonEl.disabled = true;
      }

      // Try to submit form
      const mainButton = this.productForm.querySelector(SELECTORS.mainButton);
      if (mainButton && !mainButton.disabled) {
        mainButton.click();
      } else {
        // Try fallback selectors
        let foundButton = null;
        for (const selector of MAIN_BUTTON_SELECTORS) {
          foundButton = this.productForm.querySelector(selector);
          if (foundButton && !foundButton.disabled) {
            foundButton.click();
            break;
          }
        }
        
        // Last resort: submit form directly
        if (!foundButton) {
          const submitEvent = new Event('submit', {
            bubbles: true,
            cancelable: true
          });
          
          if (this.productForm.dispatchEvent(submitEvent)) {
            this.productForm.submit();
          }
        }
      }

      // Remove loading state after delay
      this._loadingTimeout = setTimeout(() => {
        if (this.buttonEl) {
          this.buttonEl.classList.remove(CLASSES.loading);
          this.updateButtonState();
        }
        this._loadingTimeout = null;
      }, CONFIG.DELAYS.GLOBO_FINAL * 4);
    }

    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }
  }

  // Initialize when DOM is ready
  const initSticky = () => {
    try {
      new StickyAddToCart();
    } catch (e) {
      // Silently handle initialization errors
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSticky);
  } else {
    setTimeout(initSticky, 100);
  }
})();
